import * as React from 'react';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import fetch from 'logic/rest/FetchProvider';
import { qualifyUrl } from 'util/URLUtils';
import { DATA_LAKE_API_ROUTES } from 'data-lake/Constants';
import UserNotification from 'util/UserNotification';
import useUserDateTime from 'hooks/useUserDateTime';
import { getDataLakeUriParams } from 'data-lake/logic/Utils';
import { handleFailures } from 'data-lake/data-lake-overview/actions/Utils';
import type { DataLakeActionValues } from 'data-lake/Types';
import useSelectedDataLake from 'data-lake/hooks/useSelectedDataLake';
import DataLakeDeleteActionForm from 'data-lake/components/DataLakeActionModals/DataLakeDeleteActionForm';

type Props = {
  archiveStreamId: string;
  onToggleModal: () => void;
};

const DeleteArchiveModal = ({ archiveStreamId, onToggleModal }: Props) => {
  const { selectedDataLake } = useSelectedDataLake();
  const { userTimezone } = useUserDateTime();
  const queryClient = useQueryClient();

  const onDeleteSubmit = useCallback(
    (values: DataLakeActionValues) => {
      const uriParams = getDataLakeUriParams(values, userTimezone);

      fetch('POST', qualifyUrl(`${DATA_LAKE_API_ROUTES.DataLakeAPIController.bulk_delete().url}?${uriParams}`), {
        stream_ids: [archiveStreamId],
      })
        .then((response) => {
          const failures = response?.failures || [];
          handleFailures(failures, 'deleted');
        })
        .catch((error) => {
          UserNotification.error(`An error occurred while deleting archive. ${error}`);
        })
        .finally(() => {
          queryClient.invalidateQueries({
            queryKey: ['data-lake'],
          });
          onToggleModal();
        });
    },
    [userTimezone, archiveStreamId, queryClient, onToggleModal],
  );

  return (
    <DataLakeDeleteActionForm
      show
      onHide={onToggleModal}
      dataLakeId={selectedDataLake.id}
      streamIds={[archiveStreamId]}
      onSubmit={onDeleteSubmit}>
      <p>You are about to delete data from 1 Data Lake. This action cannot be undone.</p>
    </DataLakeDeleteActionForm>
  );
};

export default DeleteArchiveModal;
